import React, { useState, useEffect } from 'react';
import { FaKey, FaTrash, FaPlus } from 'react-icons/fa';
import api from '../services/api';

const translateKsefError = (msg) => {
    if (!msg) return msg;
    if (/token already exists/i.test(msg)) return 'Token KSeF już istnieje';
    if (/token not found/i.test(msg)) return 'Nie znaleziono tokenu KSeF';
    if (/not authorized/i.test(msg)) return 'Brak autoryzacji';
    if (/user not found/i.test(msg)) return 'Nie znaleziono użytkownika';
    return msg;
};

const KsefTokenManagement = () => {
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);

    // Formularz nowego tokenu
    const [newTokenValue, setNewTokenValue] = useState('');
    const [newTokenName, setNewTokenName] = useState('');
    const [newExpiresAt, setNewExpiresAt] = useState('');

    // Admin: zarządzanie tokenami innych użytkowników
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isAdmin = user.role === 'admin';

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await api.get('/users/');
                setUsers(response.data.filter(u => u.role === 'user'));
            } catch (err) {
                console.error('Failed to fetch users', err);
            }
        };

        if (isAdmin) {
            fetchUsers();
        }
    }, [isAdmin]);

    useEffect(() => {
        const fetchToken = async () => {
            try {
                setLoading(true);
                setError(null);
                const params = isAdmin && selectedUserId ? `?user_id=${selectedUserId}` : '';
                const response = await api.get(`/ksef/token${params}`);
                setToken(response.data);
            } catch (err) {
                if (err.response?.status === 404) {
                    setToken(null);
                } else {
                    console.error('Failed to fetch token', err);
                    setError('Nie udało się pobrać tokenu');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchToken();
    }, [isAdmin, selectedUserId]);

    const handleAddToken = async (e) => {
        e.preventDefault();
        setError(null);

        if (!newTokenValue.trim()) {
            setError('Token jest wymagany');
            return;
        }
        if (!newExpiresAt) {
            setError('Data wygaśnięcia jest wymagana');
            return;
        }

        try {
            const params = isAdmin && selectedUserId ? `?user_id=${selectedUserId}` : '';
            const response = await api.post(`/ksef/token${params}`, {
                token_value: newTokenValue,
                token_name: newTokenName || null,
                expires_at: new Date(newExpiresAt).toISOString()
            });
            setToken(response.data);
            setShowAddModal(false);
            resetForm();
        } catch (error) {
            console.error('Failed to add token', error);
            const detail = error.response?.data?.detail;
            if (typeof detail === 'string') {
                setError(translateKsefError(detail));
            } else if (Array.isArray(detail)) {
                setError(detail.map(e => e.msg).join(', '));
            } else {
                setError('Nie udało się dodać tokenu');
            }
        }
    };

    const handleDeleteToken = async () => {
        if (!window.confirm('Czy na pewno chcesz usunąć token KSeF?')) return;

        try {
            const params = isAdmin && selectedUserId ? `?user_id=${selectedUserId}` : '';
            await api.delete(`/ksef/token${params}`);
            setToken(null);
        } catch (error) {
            console.error('Failed to delete token', error);
            setError('Nie udało się usunąć tokenu');
        }
    };

    const resetForm = () => {
        setNewTokenValue('');
        setNewTokenName('');
        setNewExpiresAt('');
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('pl-PL');
    };

    const isTokenExpired = (expiresAt) => {
        if (!expiresAt) return false;
        return new Date(expiresAt) < new Date();
    };

    const getUserName = (userId) => {
        const foundUser = users.find(u => u.id === userId);
        return foundUser ? (foundUser.full_name || foundUser.username) : '-';
    };

    return (
        <div className="min-h-screen p-4">
            <main className="container mx-auto">
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl sm:text-2xl font-bold flex items-center">
                            <FaKey className="mr-2 text-softTeal" />
                            Zarządzanie tokenem KSeF
                        </h2>
                    </div>

                    {isAdmin && (
                        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                            <label className="block text-gray-700 mb-2 font-semibold">
                                Wybierz użytkownika:
                            </label>
                            <select
                                value={selectedUserId}
                                onChange={(e) => setSelectedUserId(e.target.value)}
                                className="w-full md:w-1/2 p-2 border border-gray-300 rounded-lg"
                            >
                                <option value="">-- Mój token --</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>
                                        {u.full_name || u.username}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                            {error}
                        </div>
                    )}

                    {loading ? (
                        <p className="text-center py-4">Ładowanie...</p>
                    ) : token ? (
                        <div className="border border-gray-200 rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <span className="font-bold text-gray-600">Nazwa tokenu:</span>
                                    <p className="mt-1">{token.token_name || '-'}</p>
                                </div>
                                <div>
                                    <span className="font-bold text-gray-600">Status:</span>
                                    <p className="mt-1">
                                        {token.is_active ? (
                                            isTokenExpired(token.expires_at) ? (
                                                <span className="text-red-600 font-semibold">Wygasły</span>
                                            ) : (
                                                <span className="text-green-600 font-semibold">Aktywny</span>
                                            )
                                        ) : (
                                            <span className="text-gray-500">Nieaktywny</span>
                                        )}
                                    </p>
                                </div>
                                <div>
                                    <span className="font-bold text-gray-600">Data utworzenia:</span>
                                    <p className="mt-1">{formatDate(token.created_at)}</p>
                                </div>
                                <div>
                                    <span className="font-bold text-gray-600">Data wygaśnięcia:</span>
                                    <p className={`mt-1 ${isTokenExpired(token.expires_at) ? 'text-red-600' : ''}`}>
                                        {formatDate(token.expires_at)}
                                    </p>
                                </div>
                                <div>
                                    <span className="font-bold text-gray-600">Ostatnie użycie:</span>
                                    <p className="mt-1">{formatDate(token.last_used_at)}</p>
                                </div>
                                <div>
                                    <span className="font-bold text-gray-600">Liczba użyć:</span>
                                    <p className="mt-1">{token.usage_count || 0}</p>
                                </div>
                            </div>

                            <div className="mt-6 flex gap-3">
                                <button
                                    onClick={handleDeleteToken}
                                    className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition flex items-center"
                                >
                                    <FaTrash className="mr-2" /> Usuń token
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <p className="text-gray-500 mb-4">
                                {isAdmin && selectedUserId
                                    ? `Użytkownik ${getUserName(parseInt(selectedUserId))} nie ma jeszcze tokenu KSeF.`
                                    : 'Nie masz jeszcze tokenu KSeF.'
                                }
                            </p>
                            <button
                                onClick={() => setShowAddModal(true)}
                                className="bg-softTeal text-white px-6 py-2 rounded-lg hover:bg-warmOrange transition flex items-center mx-auto"
                            >
                                <FaPlus className="mr-2" /> Dodaj token
                            </button>
                        </div>
                    )}
                </div>
            </main>

            {/* Modal dodawania tokenu */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
                        <h3 className="text-xl font-bold mb-4">Dodaj token KSeF</h3>

                        <form onSubmit={handleAddToken}>
                            <div className="mb-4">
                                <label className="block text-gray-700 mb-2">
                                    Token: <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={newTokenValue}
                                    onChange={(e) => setNewTokenValue(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                    rows="3"
                                    placeholder="Wklej token KSeF"
                                    required
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block text-gray-700 mb-2">Nazwa tokenu:</label>
                                <input
                                    type="text"
                                    value={newTokenName}
                                    onChange={(e) => setNewTokenName(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                    placeholder="Opcjonalna nazwa"
                                />
                            </div>

                            <div className="mb-6">
                                <label className="block text-gray-700 mb-2">
                                    Data wygaśnięcia: <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="datetime-local"
                                    value={newExpiresAt}
                                    onChange={(e) => setNewExpiresAt(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                    required
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    type="submit"
                                    className="flex-1 bg-softTeal text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition"
                                >
                                    Dodaj
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAddModal(false);
                                        resetForm();
                                    }}
                                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition"
                                >
                                    Anuluj
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KsefTokenManagement;
